import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { fmtMoney, fmtHours } from './format';
import { c, chart, t } from '../../theme';

// ── This month against the same month last year ──────────────────────────────
//
// Paired bars rather than two lines: the comparison being made is one month
// against one other month, not a shape against a shape. Reading "January 2026
// against January 2025" off two overlaid twelve-point lines means tracing back
// twelve positions, which nobody does in a meeting.
//
// ONE MEASURE AT A TIME, and no second axis ever. Hours and dollars belong to
// different scales, and drawing them together on twin axes lets the reader pick
// whichever crossing point suits the argument. The toggle is the whole reason
// three measures can live on one chart honestly:
//
//   OT hours     the volume. What actually happened.
//   OT cost      the money. Moves with wage rates as well as with hours, so it
//                is never the measure to read a behavior change from.
//   OT % of hrs  the rate. Immune to both the pay-period count and to wage
//                growth, which makes it the only one comparable on its face.
//
// A month with a different number of pay periods than its prior year is hatched
// and carries a caret. That is not decoration: under weekly payroll a third of
// month pairs are 4-against-5, and the raw bars are ~25% apart on identical
// overtime. The footnote says so in words, because a legend entry nobody reads
// is not a warning.
//
// A month whose prior year has no payroll loaded draws ONE bar, not a bar and a
// zero. A zero-height bar beside a real one reads as "there was none last
// year"; a missing bar reads as "we do not know", which is the truth until the
// backfill lands.
//
// Two of those are not the same, and only one is a warning. With history
// starting in January 2025, no month of 2025 will ever have a 2024 beside it --
// nothing is missing and nothing is coming, so the chart says where the record
// starts rather than flagging a third of its own bars as broken. A hole INSIDE
// the loaded range still warns, because that one is somebody's missing upload.
//
// The newest month is usually PARTIAL -- one run in with four still to come --
// and drawn whole it is the most misleading mark on the chart: a fifth of a
// month at the right-hand edge, where the eye lands, reading as a collapse in
// overtime. It is drawn hatched, labelled, and excluded from the comparison.

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// AN ORDINAL RAMP, NOT TWO CATEGORICAL HUES. Years are ordered, so hue is the
// wrong channel for them -- swapping 2025 and 2026 would change the meaning,
// which is the test for ordinal. One navy hue stepped light to dark carries the
// order itself, and the newest year is always the darkest bar. This is the same
// reasoning, and the same ramp, as Expense Analysis's year-over-year chart; two
// year-comparison views that colour years differently teach the reader nothing.
//
// Assigned from the DARK end so "darkest is most recent" holds however many
// years are on screen.
const PRIOR = chart.sequential[1];
const CUR   = chart.sequential[3];

export const MEASURES = {
  hours:  { key: 'hours',  label: 'OT hours',      axis: (v) => fmtHours(v),
            fmt: fmtHours, cur: (p) => p.ot_hours,  prior: (p) => p.prior.ot_hours },
  amount: { key: 'amount', label: 'OT cost',       axis: (v) => `$${Math.round(v / 1000)}k`,
            fmt: fmtMoney, cur: (p) => p.ot_amount, prior: (p) => p.prior.ot_amount },
  pct:    { key: 'pct',    label: 'OT % of hours', axis: (v) => `${v}%`,
            fmt: (v) => (v === null || v === undefined ? '—' : `${v}%`),
            cur: (p) => p.ot_pct,   prior: (p) => p.prior.ot_pct },
};

const shortLabel = (ym) => `${MONTHS_SHORT[Number(ym.slice(5, 7)) - 1]} ’${ym.slice(2, 4)}`;

function YoyTooltip({ active, payload, measure }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const m = MEASURES[measure];
  const runs = (n) => `${n} pay period${n === 1 ? '' : 's'}`;

  return (
    <div style={s.tip}>
      <div style={s.tipTitle}>{p.curLabel}</div>
      <div style={s.tipRow}>
        <span style={{ ...s.dot, background: CUR }} />
        <span style={s.tipKey}>{p.curLabel}</span>
        <span style={s.tipVal}>{m.fmt(p.cur)}</span>
      </div>
      <div style={s.tipRow}>
        <span style={{ ...s.dot, background: PRIOR }} />
        <span style={s.tipKey}>{p.priorLabel}</span>
        <span style={s.tipVal}>{p.priorLoaded ? m.fmt(p.prior) : 'not loaded'}</span>
      </div>
      <div style={s.tipMeta}>
        {runs(p.periods)} vs {p.priorLoaded ? runs(p.priorPeriods) : '—'}
      </div>
      {p.partial && (
        <div style={s.tipWarn}>
          Month still running — {p.periods} of about {p.expected} runs in. Not a full month.
        </div>
      )}
      {!p.partial && p.priorLoaded && !p.comparable && (
        <div style={s.tipWarn}>
          Different number of pay periods — read the per-period figure, not this gap.
        </div>
      )}
      {!p.priorLoaded && p.beforeHistory && (
        <div style={s.tipMeta}>Earlier than the first payroll loaded — no prior year exists.</div>
      )}
    </div>
  );
}

export default function YoyTrend({ points = [], measure = 'hours', isLoading, error }) {
  const m = MEASURES[measure] || MEASURES.hours;

  const rows = points.map((p) => ({
    label:        shortLabel(p.month),
    curLabel:     p.label,
    priorLabel:   p.prior.label,
    cur:          m.cur(p),
    // Recharts draws a 0 bar for null, so an unloaded prior year is fed as
    // undefined -- which it omits entirely. That distinction is the whole point.
    prior:        p.prior.loaded ? m.prior(p) : undefined,
    priorLoaded:  p.prior.loaded,
    beforeHistory: p.prior.before_history,
    periods:      p.periods,
    priorPeriods: p.prior.periods,
    comparable:   p.yoy.comparable,
    partial:      p.partial,
    expected:     p.expected_periods,
  }));

  const anyMismatch = rows.some((r) => r.priorLoaded && !r.comparable && !r.partial);
  // Only a hole inside the loaded range is worth a warning.
  const anyMissing  = rows.some((r) => !r.priorLoaded && !r.beforeHistory);
  const partialRow  = rows.find((r) => r.partial);
  const historyFrom = rows.find((r) => r.beforeHistory);

  if (isLoading) return <div style={s.card}><div style={s.empty}>Loading…</div></div>;
  if (error)     return <div style={s.card}><div style={s.empty}>Could not load the comparison.</div></div>;
  if (!rows.length) return <div style={s.card}><div style={s.empty}>No payroll loaded yet.</div></div>;

  return (
    <div style={s.card}>
      <div style={{ width: '100%', height: 300 }}>
        <ResponsiveContainer>
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 4, bottom: 4 }} barGap={2}>
            <CartesianGrid stroke={c.border} vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: c.textMuted }}
                   axisLine={{ stroke: c.border }} tickLine={false} />
            <YAxis tickFormatter={m.axis} tick={{ fontSize: 11, fill: c.textMuted }}
                   axisLine={false} tickLine={false} width={54} />
            <Tooltip content={<YoyTooltip measure={measure} />} cursor={{ fill: c.surfaceSunk }} />
            <Legend iconType="circle" iconSize={8}
                    wrapperStyle={{ fontSize: 12, color: c.textMuted, paddingTop: 4 }} />
            <defs>
              {/* The partial month is the same navy, broken up, so it still reads
                  as "this year" while never being mistaken for a whole month.
                  Texture rather than a second colour: colour is carrying the
                  year here, and a third hue would say there is a third series. */}
              <pattern id="yoyPartial" patternUnits="userSpaceOnUse" width="6" height="6"
                       patternTransform="rotate(45)">
                <rect width="6" height="6" fill={c.surface} />
                <rect width="3" height="6" fill={CUR} />
              </pattern>
            </defs>
            <Bar dataKey="prior" name="Prior year" fill={PRIOR} radius={[4, 4, 0, 0]} maxBarSize={26}
                 isAnimationActive={false} />
            <Bar dataKey="cur"   name="This year"  fill={CUR}   radius={[4, 4, 0, 0]} maxBarSize={26}
                 isAnimationActive={false}>
              {rows.map((r) => (
                <Cell key={r.label} fill={r.partial ? 'url(#yoyPartial)' : CUR}
                      stroke={r.partial ? CUR : undefined} strokeWidth={r.partial ? 1 : 0} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {(anyMismatch || anyMissing || partialRow || historyFrom) && (
        <div style={s.notes}>
          {partialRow && (
            <div style={s.note}>
              <strong style={s.noteLead}>{partialRow.curLabel} is still running.</strong>{' '}
              {partialRow.periods} of about {partialRow.expected} payroll runs have landed, so its
              hatched bar is a part-month drawn beside whole ones. It is excluded from the
              comparison until the month closes.
            </div>
          )}
          {anyMismatch && (
            <div style={s.note}>
              <strong style={s.noteLead}>Unequal pay periods.</strong> Some months above hold a
              different number of payroll runs than the same month last year. Those bars differ by
              the extra run as well as by overtime — compare the per-period column in the table
              below, or switch this chart to <em>OT % of hours</em>.
            </div>
          )}
          {anyMissing && (
            <div style={s.note}>
              <strong style={s.noteLead}>Missing prior year.</strong> Some months inside the loaded
              range have no payroll for the year before, so they are drawn without a comparison
              rather than against zero. That is a gap in the upload history, not a quiet month.
            </div>
          )}
          {historyFrom && (
            <div style={s.note}>
              <strong style={s.noteLead}>Single bars at the left</strong> are earlier than the
              first payroll we hold, so there is no prior year for them to be compared against.
              Nothing is missing.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const s = {
  card:  { background: c.surface, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '14px 12px 8px' },
  empty: { padding: '48px 0', textAlign: 'center', color: c.textMuted, ...t.bodySm },

  notes:    { borderTop: `1px solid ${c.border}`, marginTop: '10px', padding: '10px 4px 4px',
              display: 'flex', flexDirection: 'column', gap: '6px' },
  note:     { ...t.caption, color: c.textMuted },
  noteLead: { color: c.textStrong },

  tip:      { background: c.surface, border: `1px solid ${c.borderStrong}`, borderRadius: '8px',
              padding: '9px 11px', boxShadow: '0 4px 14px rgba(17,24,39,0.10)', minWidth: '210px' },
  tipTitle: { ...t.bodySm, fontWeight: 600, color: c.text, marginBottom: '6px' },
  tipRow:   { display: 'flex', alignItems: 'center', gap: '7px', ...t.caption, marginTop: '3px' },
  dot:      { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  tipKey:   { color: c.textMuted, flex: 1 },
  tipVal:   { color: c.text, fontWeight: 600, ...t.tabular },
  tipMeta:  { ...t.micro, color: c.textFaint, marginTop: '7px',
              borderTop: `1px solid ${c.border}`, paddingTop: '6px' },
  tipWarn:  { ...t.micro, color: c.warningText, marginTop: '5px' },
};
